import type { Point } from "@incytestudios/ddv";
import {
  getMaxCurrentAtTemperatureA,
  getCableMaxTemperatureF
} from "../../calculations/cables";
import type {
  CableTemperatureChartContext,
  CableTemperatureChartDataSet
} from "./d";

export const calculateCableTemperatureDataSets = (
  context: CableTemperatureChartContext
): CableTemperatureChartDataSet[] => {
  const {
    equipment,
    cableCalculations,
    cables,
    minTemp,
    targetTempUnit,
    motorCalculations
  } = context;
  if (!equipment) return [];

  const out: CableTemperatureChartDataSet[] = [];

  equipment.cables_order?.forEach((cableId: string) => {
    const calc = cableCalculations.individual[cableId];
    const form = cables[cableId];
    if (!calc || !form || !form.r_cable || form.journal_deleted_at) return;

    const maxTemp = form.r_cable?.max_temperature_fahrenheit ?? 500;
    const ampFactor: number = form.r_cable?.amp_factor ?? 0;
    const loops = 20;
    const tempPerStep = (maxTemp - minTemp) / loops;
    const points: Point[] = [];

    for (let temp = minTemp; temp <= maxTemp; temp += tempPerStep) {
      const xValue = targetTempUnit.toThisUnit(temp);
      const yValue = getMaxCurrentAtTemperatureA(maxTemp, temp, ampFactor);

      if (xValue >= 0) {
        // Check if the x value is non-negative
        points.push({
          x: xValue,
          y: yValue
        });
      }
    }

    // find the operating point
    const operatingAmp = motorCalculations.motorCurrentA.value;
    const xOperatingPoint = getCableMaxTemperatureF(
      ampFactor,
      operatingAmp,
      calc.downholeTempAtCableEndF
    );
    const maxAmpsAtTemp = getMaxCurrentAtTemperatureA(
      maxTemp,
      xOperatingPoint,
      ampFactor
    );
    const loadRatio = maxAmpsAtTemp > 0 ? operatingAmp / maxAmpsAtTemp : 1;

    // push the amps over temp points
    out.push({
      title: calc.cableHeader,
      xAxisLabel: `Temperature (${
        targetTempUnit.shortLabel || targetTempUnit.label
      })`,
      yAxisLabel: "Load (A)",
      operatingPoint: {
        label: calc.cableHeader,
        xLabel: `Temp ${targetTempUnit.shortLabel}`,
        point: {
          x: targetTempUnit.toThisUnit(xOperatingPoint),
          y: operatingAmp
        }
      },
      loadRatio,
      points
    });
  });
  return out;
};
